import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from "react-select";
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const EditOnService = ({ show, onClose, editData, onUpdated }) => {
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const [service, setService] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [cost, setCost] = useState("");
  const [vendorChecked, setVendorChecked] = useState(false);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const unitOptions = [
    { value: "1", label: "gms" },
    { value: "2", label: "Kg's" },
    { value: "3", label: "Ltr's" },
    { value: "4", label: "ton's" },
    { value: "5", label: "No's" },
    { value: "6", label: "cm" },
  ];

  useEffect(() => {
    if (editData) {
      setService(editData.service || "");
      setSelectedOption(unitOptions.find(opt => opt.value === String(editData.quantity)) || null);
      setCost(editData.cost_per_unit !== undefined ? editData.cost_per_unit : "");
      setVendorChecked(editData.vendor === 1);
      setDescription(editData.description || "");
      setErrors({});
    }
  }, [editData]);

  const validate = () => {
    const errs = {};
    if (!service.trim()) errs.service = "Service is required.";
    if (!selectedOption) errs.unit = "Quantity Unit is required.";
    if (cost.trim() === "") {
      errs.cost = "Cost per unit is required.";
    } else if (isNaN(parseFloat(cost))) {
      errs.cost = "Cost must be a valid number.";
    }
    return errs;
  };

  const handleUpdate = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    try {
      const payload = {
        service: service.trim(),
        unit: selectedOption.value,
        cost_per_unit: parseFloat(cost),
        vendor: vendorChecked ? 1 : 0,
        description: description.trim(),
      };

      await axios.put(`${baseURL}/addonservice/update_service/${editData.sno}`, payload);

      toast.success("Service updated successfully!");
      if (onUpdated) onUpdated();
      onClose();
    } catch (error) {
      console.error("Update failed", error);
      toast.error("Failed to update service. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} size="md" onHide={onClose} backdrop="static" keyboard={!loading}>
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
            <h3 className="modal-title text-white">Update Add-On Service</h3>
            <Button variant="" type="button" className="close px-6 py-2" onClick={onClose} disabled={loading}>
              <span className="text-white" aria-label="Close modal">&times;</span>
            </Button>
          </div>
          <div className="modal-body">
            <div className="row">
              {/* Service */}
              <div className="col-lg-12 mb-2">
                <label htmlFor="serviceInput" className="text-black font-w500 fs-7">
                  Service<span className="required">*</span>
                </label>
                <input
                  id="serviceInput"
                  name="service"
                  type="text"
                  className={`form-control ${errors.service ? 'is-invalid' : ''}`}
                  placeholder="Enter Service"
                  value={service}
                  onChange={e => setService(e.target.value)}
                  autoFocus
                  aria-invalid={!!errors.service}
                />
                {errors.service && <div className="invalid-feedback d-block">{errors.service}</div>}
              </div>

              {/* Unit */}
              <div className="col-lg-12 mb-2">
                <label htmlFor="unitSelect" className="text-black font-w500 fs-7">
                  Quantity Unit<span className="required">*</span>
                </label>
                <Select
                  inputId="unitSelect"
                  placeholder="Select Unit"
                  value={selectedOption}
                  onChange={setSelectedOption}
                  options={unitOptions}
                  classNamePrefix={errors.unit ? 'is-invalid' : ''}
                  aria-invalid={!!errors.unit}
                  isDisabled={loading}
                />
                {errors.unit && <div className="invalid-feedback d-block">{errors.unit}</div>}
              </div>

              {/* Cost */}
              <div className="col-lg-12 mb-2">
                <label htmlFor="costInput" className="text-black font-w500 fs-7">
                  Cost (per Unit)<span className="required">*</span>
                </label>
                <input
                  id="costInput"
                  name="cost"
                  type="number"
                  min="0"
                  step="0.01"
                  className={`form-control ${errors.cost ? 'is-invalid' : ''}`}
                  placeholder="Enter Cost"
                  value={cost}
                  onChange={e => setCost(e.target.value)}
                  aria-invalid={!!errors.cost}
                  disabled={loading}
                />
                {errors.cost && <div className="invalid-feedback d-block">{errors.cost}</div>}
              </div>

              {/* Vendor */}
              <div className="col-lg-12 mb-3 d-flex align-items-center">
                <input
                  id="vendorCheckbox"
                  className="form-check-input me-2"
                  type="checkbox"
                  checked={vendorChecked}
                  onChange={e => setVendorChecked(e.target.checked)}
                  disabled={loading}
                />
                <label htmlFor="vendorCheckbox" className="fs-7 text-black mb-0 font-w500">
                  Vendor
                </label>
              </div>

              {/* Description */}
              <div className="col-lg-12 mb-2">
                <label htmlFor="descriptionTextarea" className="text-black font-w500 fs-7">Description</label>
                <textarea
                  id="descriptionTextarea"
                  rows={4}
                  className="form-control"
                  placeholder="Enter Description"
                  value={description}
                  onChange={(e) => {
                                    let val = e.target.value;
                                    if (val.length > 0) {
                                        val = val.charAt(0).toUpperCase() + val.slice(1);
                                    }
                                    setDescription(val);
                                }}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-4">
              <Button
                variant="primary"
                size="sm"
                onClick={handleUpdate}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Add-On Service'}
                <i className="fas fa-angle-double-right ms-2"></i>
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default EditOnService;
